import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Account, Folder } from '@/types/email';
import { UNIFIED_ACCOUNT_ID, STANDARD_UNIFIED_ROLES, type StandardUnifiedRole, getStandardRoleDisplayName } from '@/constants/mailbox';
import type { FolderVisibility } from '@/api/folders';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
} from '@/components/ui/context-menu';
import { SyncProgressBar, type SyncProgressBarProps } from '@/components/SyncProgressBar';
import { buildUnifiedFolders } from '@/lib/mailboxAggregations';
import {
  Inbox,
  Send,
  FileText,
  AlertOctagon,
  Trash2,
  Archive,
  PenSquare,
  ChevronRight,
  LogOut,
  X,
  Settings,
  PlusCircle,
  CalendarPlus,
  MailCheck,
  Eye,
  FolderClock,
  Pencil,
  MoreHorizontal,
  FilePlus
} from 'lucide-react';
import { cn, navActiveClass } from '@/lib/utils';
import { useAuth } from '@/contexts/useAuth';
import { motion } from 'motion/react';
import { easings, durations } from '@/lib/animations';
import { useUIStore } from '@/stores/uiStore';
import { useScrollRestoration } from '@/lib/scrollRestoration';
import { TodoSidebarContent } from '@/features/todo/components/TodoSidebar';
import { CalendarSidebarContent } from '@/features/calendar/components/CalendarSidebar';
import { NotesSidebarContent } from '@/features/notes/components/NotesSidebar';
import { RecentOpensPopover } from '@/components/RecentOpensPopover';

interface SidebarProps {
  accounts: Account[];
  selectedAccountId: string | null;
  folders: Folder[];
  accountFoldersById: Record<string, Folder[]>;
  selectedFolderId: string;
  onFolderSelect: (accountId: string, folderId: string) => void;
  onFolderHover?: (accountId: string, folderId: string) => void;
  onOpenSettings: (tab?: 'general' | 'contacts' | 'rules' | 'signatures' | 'accounts' | 'folders') => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  syncIndicators?: SyncProgressBarProps[] | null;
  onEmailDrop?: (folder: Folder) => void;
  onFolderDragEnter?: (folder: Folder) => void;
  onFolderDragLeave?: (folder: Folder) => void;
  activeDropFolderId?: string | null;
  isDraggingEmail?: boolean;
  canDropEmail?: (folder: Folder) => boolean;
  onMarkFolderAsRead?: (folders: Array<{ accountId: string; folderId: string }>) => void;
  onSetFolderVisibility?: (accountId: string, folderId: string, visibility: FolderVisibility) => void;
  onRenameFolder?: (accountId: string, folderId: string, currentName: string) => void;
  /**
   * Replaces the scrollable nav body (folder tree / workspace content).
   * Sub-view pages (Triage/Surveys) supply their own navigation while
   * keeping the shared switcher, compose button, and footer.
   */
  contentOverride?: ReactNode;
}

type SourceRef = { accountId: string; folderId: string };

const standardRoleIconMap: Record<StandardUnifiedRole, React.ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  sent: Send,
  drafts: FileText,
  spam: AlertOctagon,
  trash: Trash2,
  archive: Archive,
};

const fallbackIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  send: Send,
  'file-text': FileText,
  'alert-octagon': AlertOctagon,
  'trash-2': Trash2,
  archive: Archive,
};

function getInitials(account: Account): string {
  const source = account.displayName || account.emailAddress || '';
  const parts = source.trim().split(/\s+/).slice(0, 2);
  if (!parts.length) {
    return '?';
  }
  return parts.map((part) => part.charAt(0).toUpperCase()).join('');
}

function getFolderSources(folder: Folder): SourceRef[] {
  if (folder.sourceFolders && folder.sourceFolders.length > 0) {
    return folder.sourceFolders;
  }
  if (folder.accountId && folder.accountId !== UNIFIED_ACCOUNT_ID) {
    return [{ accountId: folder.accountId, folderId: folder.id }];
  }
  return [];
}

function getFolderKey(folder: Folder): string {
  const sources = getFolderSources(folder);
  if (sources.length === 1) {
    const [source] = sources;
    return `${source.accountId}:${source.folderId}`;
  }
  if (sources.length > 1) {
    return folder.id;
  }
  return folder.id;
}

export function Sidebar({
  accounts,
  selectedAccountId,
  folders,
  accountFoldersById,
  selectedFolderId,
  onFolderSelect,
  onFolderHover,
  onOpenSettings,
  isMobileOpen,
  onMobileClose,
  syncIndicators,
  onEmailDrop,
  onFolderDragEnter,
  onFolderDragLeave,
  activeDropFolderId,
  isDraggingEmail = false,
  canDropEmail,
  onMarkFolderAsRead,
  onSetFolderVisibility,
  onRenameFolder,
  contentOverride,
}: SidebarProps) {
  const { logout } = useAuth();
  const activeWorkspace = useUIStore((state) => state.activeWorkspace);
  const mailView = useUIStore((state) => state.mailView);
  // Each page mounts its own Sidebar, so the rail's scroll offset would reset
  // on every tab switch; restore it per workspace/page.
  const railViewportRef = useRef<HTMLDivElement | null>(null);
  useScrollRestoration({
    scopeKey: `sidebar:${activeWorkspace}:${mailView}`,
    viewportRef: railViewportRef,
  });
  const isTodoWorkspace = activeWorkspace === 'todo';
  const isCalendarWorkspace = activeWorkspace === 'calendar';
  const isNotesWorkspace = activeWorkspace === 'notes';

  const actualAccounts = useMemo(
    () => accounts.filter((account) => account.id !== UNIFIED_ACCOUNT_ID),
    [accounts],
  );

  const accountsById = useMemo(
    () =>
      accounts.reduce<Record<string, Account>>((acc, account) => {
        acc[account.id] = account;
        return acc;
      }, {}),
    [accounts],
  );

  const unifiedFolders = useMemo(
    () => buildUnifiedFolders(accountFoldersById, actualAccounts),
    [accountFoldersById, actualAccounts],
  );

  const unifiedFolderMap = useMemo(() => {
    const map = new Map<string, Folder>();
    unifiedFolders.forEach((folder) => {
      map.set(folder.id, folder);
    });
    return map;
  }, [unifiedFolders]);

  const customFolderSource = useMemo(() => {
    if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
      return folders;
    }
    return unifiedFolders;
  }, [folders, selectedAccountId, unifiedFolders]);

  const systemGroups = useMemo(() => {
    return STANDARD_UNIFIED_ROLES.map((role) => {
      const unifiedId = `${UNIFIED_ACCOUNT_ID}:${role}`;
      const unifiedFolder = unifiedFolderMap.get(unifiedId);
      if (!unifiedFolder) {
        return null;
      }
      const entries = (unifiedFolder.sourceFolders ?? []).map((source) => {
        const account = accountsById[source.accountId];
        if (!account) {
          return null;
        }
        const accountFolders = accountFoldersById[source.accountId] ?? [];
        const folder = accountFolders.find((candidate) => candidate.id === source.folderId);
        if (!folder) {
          return null;
        }
        return { account, folder };
      }).filter((value): value is { account: Account; folder: Folder } => Boolean(value));

      entries.sort((a, b) => {
        const aName = a.account.displayName || a.account.emailAddress || '';
        const bName = b.account.displayName || b.account.emailAddress || '';
        return aName.localeCompare(bName);
      });

      return {
        role,
        unifiedFolder,
        accountFolders: entries,
      };
    }).filter((value): value is {
      role: StandardUnifiedRole;
      unifiedFolder: Folder;
      accountFolders: Array<{ account: Account; folder: Folder }>;
    } => Boolean(value));
  }, [accountsById, accountFoldersById, unifiedFolderMap]);

  const accountSystemFolderIds = useMemo(() => {
    const ids = new Set<string>();
    systemGroups.forEach((group) => {
      group.accountFolders.forEach(({ folder }) => {
        ids.add(folder.id);
      });
    });
    return ids;
  }, [systemGroups]);

  const standardFolderIds = useMemo(() => {
    return new Set(STANDARD_UNIFIED_ROLES.map((role) => `${UNIFIED_ACCOUNT_ID}:${role}`));
  }, []);

  const getFolderVisibility = useCallback((folder: Folder): FolderVisibility => {
    // New visibility property takes precedence
    if (folder.visibility) {
      return folder.visibility;
    }
    // Fallback to legacy hidden property
    if (folder.hidden) {
      return 'more';
    }
    return 'always';
  }, []);

  const customFolders = useMemo(() => {
    return customFolderSource.filter(
      (folder) => !standardFolderIds.has(folder.id) && !accountSystemFolderIds.has(folder.id),
    );
  }, [accountSystemFolderIds, customFolderSource, standardFolderIds]);

  const visibleCustomFolders = useMemo(
    () => customFolders.filter((folder) => getFolderVisibility(folder) === 'always'),
    [customFolders, getFolderVisibility]
  );
  const moreCustomFolders = useMemo(
    () => customFolders.filter((folder) => getFolderVisibility(folder) === 'more'),
    [customFolders, getFolderVisibility]
  );
  // 'never' visibility folders are completely excluded

  const selectedCompositeKey = useMemo(() => {
    if (!selectedFolderId) {
      return null;
    }
    if (selectedAccountId && selectedAccountId !== UNIFIED_ACCOUNT_ID) {
      return `${selectedAccountId}:${selectedFolderId}`;
    }
    return selectedFolderId;
  }, [selectedAccountId, selectedFolderId]);

  const getCompositeKeyForCustomFolder = useCallback((folder: Folder) => {
    const sources = getFolderSources(folder);
    if (sources.length === 1 && sources[0].accountId !== UNIFIED_ACCOUNT_ID) {
      const [source] = sources;
      return `${source.accountId}:${source.folderId}`;
    }
    return folder.id;
  }, []);

  const [expandedGroups, setExpandedGroups] = useState<Partial<Record<StandardUnifiedRole, boolean>>>({});
  const autoExpandedByRole = useMemo(() => {
    const result: Partial<Record<StandardUnifiedRole, boolean>> = {};
    systemGroups.forEach((group) => {
      const hasSelectedAccountFolder = group.accountFolders.some(
        ({ account, folder }) => account.id === selectedAccountId && folder.id === selectedFolderId,
      );
      result[group.role] = hasSelectedAccountFolder;
    });
    return result;
  }, [systemGroups, selectedAccountId, selectedFolderId]);
  const [moreExpanded, setMoreExpanded] = useState(false);
  const [expandedCustomGroups, setExpandedCustomGroups] = useState<Record<string, boolean>>({});
  const forcedMoreExpanded = useMemo(
    () =>
      moreCustomFolders.some(
        (folder) => getCompositeKeyForCustomFolder(folder) === selectedCompositeKey,
      ),
    [moreCustomFolders, getCompositeKeyForCustomFolder, selectedCompositeKey],
  );
  const isMoreSectionExpanded = moreExpanded || forcedMoreExpanded;

  const toggleGroup = useCallback(
    (role: StandardUnifiedRole) => {
      setExpandedGroups((prev) => {
        const next = { ...prev };
        const manualState = next[role];
        const fallbackState = autoExpandedByRole[role] ?? false;
        const effectiveState = manualState ?? fallbackState;
        const nextState = !effectiveState;
        if (nextState === fallbackState) {
          delete next[role];
        } else {
          next[role] = nextState;
        }
        return next;
      });
    },
    [autoExpandedByRole],
  );

  const toggleCustomGroupExpansion = useCallback((folderId: string, autoState: boolean) => {
    setExpandedCustomGroups((prev) => {
      const next = { ...prev };
      const manualState = next[folderId];
      const effectiveState = manualState ?? autoState;
      const updatedState = !effectiveState;
      if (updatedState === autoState) {
        delete next[folderId];
      } else {
        next[folderId] = updatedState;
      }
      return next;
    });
  }, []);

  const handleUnifiedClick = useCallback((folder: Folder) => {
    onFolderSelect(UNIFIED_ACCOUNT_ID, folder.id);
    onMobileClose?.();
  }, [onFolderSelect, onMobileClose]);

  const handleAccountFolderClick = useCallback((accountId: string, folderId: string) => {
    onFolderSelect(accountId, folderId);
    onMobileClose?.();
  }, [onFolderSelect, onMobileClose]);

  const primaryActionContext = useUIStore((state) => state.primaryActionContext);
  const triggerPrimaryAction = useUIStore((state) => state.triggerPrimaryAction);

  const handlePrimaryActionClick = () => {
    triggerPrimaryAction();
    onMobileClose?.();
  };

  const primaryActionLabel =
    primaryActionContext === 'task'
      ? 'New Task'
      : primaryActionContext === 'event'
        ? 'New Event'
        : primaryActionContext === 'note'
          ? 'New Note'
          : 'Compose';

  const renderFolderButton = (
    folder: Folder,
    options: {
      key?: React.Key;
      label: React.ReactNode;
      icon?: React.ReactNode;
      badge?: React.ReactNode;
      isActive?: boolean;
      className?: string;
      onClick: () => void;
      onHover?: () => void;
      contextMenu?: {
        folders: Array<{ accountId: string; folderId: string }>;
        visibility?: FolderVisibility;
        canHide?: boolean;
        role?: string | null;
        unifiedRole?: string | null;
        folderName?: string;
        isSystemFolder?: boolean;
      };
    },
  ) => {
    const folderKey = getFolderKey(folder);
    const resolvedKey = options.key ?? folderKey;
    const isActiveDrop = activeDropFolderId === folderKey && isDraggingEmail;
    const dropEnabled = !isDraggingEmail || canDropEmail?.(folder) !== false;

    const buttonContent = (
      <motion.div
        key={resolvedKey}
        animate={
          isActiveDrop
            ? {
                scale: 1.03,
              }
            : isDraggingEmail && dropEnabled
              ? {
                  scale: 1,
                }
              : {
                  scale: 1,
                }
        }
        transition={{
          ...easings.spring,
          duration: durations.fast,
        }}
        whileHover={
          !isDraggingEmail || dropEnabled
            ? {
                scale: 1.01,
                transition: { duration: durations.fast },
              }
            : undefined
        }
      >
        <Button
          variant={options.isActive ? 'secondary' : 'ghost'}
          className={cn(
            'w-full justify-start gap-3 font-normal transition-all duration-200',
            options.isActive && navActiveClass,
            isActiveDrop && 'ring-2 ring-primary bg-primary/10 shadow-lg',
            isDraggingEmail && dropEnabled && !isActiveDrop && 'ring-1 ring-primary/30',
            isDraggingEmail && !dropEnabled && 'opacity-50 cursor-not-allowed',
            options.className,
          )}
          onClick={() => {
            if (isDraggingEmail && !dropEnabled) {
              return;
            }
            options.onClick();
          }}
          // Prefetch on mouse hover only — a finger has no hover, and on iOS
          // a mouseenter side effect can cost the tap its click.
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') {
              options.onHover?.();
            }
          }}
          onFocus={() => options.onHover?.()}
          onDragEnter={(event) => {
            if (!isDraggingEmail || !dropEnabled) {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            onFolderDragEnter?.(folder);
          }}
          onDragOver={(event) => {
            if (!isDraggingEmail || !dropEnabled) {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = 'move';
            // Continuously update the active drop target to handle child element transitions
            onFolderDragEnter?.(folder);
          }}
          onDragLeave={(event) => {
            if (!isDraggingEmail) {
              return;
            }
            event.stopPropagation();
            const currentTarget = event.currentTarget;
            const relatedTarget = event.relatedTarget as Node | null;
            // Only clear if we're actually leaving the button, not just moving to a child
            if (!currentTarget.contains(relatedTarget)) {
              onFolderDragLeave?.(folder);
            }
          }}
          onDrop={(event) => {
            if (!isDraggingEmail || !dropEnabled) {
              return;
            }
            event.preventDefault();
            onEmailDrop?.(folder);
            onFolderDragLeave?.(folder);
          }}
        >
          {options.icon}
          <span className="flex-1 truncate text-left">{options.label}</span>
          {options.badge}
        </Button>
      </motion.div>
    );

    // If no context menu options provided, return button without context menu
    if (!options.contextMenu) {
      return buttonContent;
    }

    const { folders: contextFolders, visibility, canHide, role, unifiedRole, folderName, isSystemFolder } = options.contextMenu;
    const firstFolder = contextFolders[0];
    const SYSTEM_ROLES = ['inbox', 'sent', 'drafts', 'spam', 'trash', 'archive', 'starred', 'important'];
    const SYSTEM_FOLDER_NAMES = ['inbox', 'sent', 'drafts', 'spam', 'trash', 'archive', 'starred', 'important', 'all mail', 'junk'];
    const normalizedRole = role?.toLowerCase() ?? '';
    const normalizedUnifiedRole = unifiedRole?.toLowerCase() ?? '';
    const normalizedName = folderName?.toLowerCase() ?? '';
    const isSystemByRole = normalizedRole && SYSTEM_ROLES.includes(normalizedRole);
    const isSystemByUnifiedRole = normalizedUnifiedRole && SYSTEM_ROLES.includes(normalizedUnifiedRole);
    const isSystemByName = normalizedName && SYSTEM_FOLDER_NAMES.includes(normalizedName);
    const canRename = firstFolder && onRenameFolder && !isSystemFolder && !isSystemByRole && !isSystemByUnifiedRole && !isSystemByName;

    return (
      <ContextMenu key={resolvedKey}>
        <ContextMenuTrigger asChild>
          {buttonContent}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          {onMarkFolderAsRead && contextFolders.length > 0 && (
            <ContextMenuItem
              onClick={() => onMarkFolderAsRead(contextFolders)}
            >
              <MailCheck className="h-4 w-4" />
              Mark all as read
            </ContextMenuItem>
          )}
          {canHide && onSetFolderVisibility && firstFolder && (
            <ContextMenuSub>
              <ContextMenuSubTrigger>
                <Eye className="h-4 w-4" />
                Visibility
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                <ContextMenuRadioGroup value={visibility ?? 'always'}>
                  <ContextMenuRadioItem
                    value="always"
                    onClick={() => onSetFolderVisibility(firstFolder.accountId, firstFolder.folderId, 'always')}
                  >
                    Always show in sidebar
                  </ContextMenuRadioItem>
                  <ContextMenuRadioItem
                    value="more"
                    onClick={() => onSetFolderVisibility(firstFolder.accountId, firstFolder.folderId, 'more')}
                  >
                    Show in More section
                  </ContextMenuRadioItem>
                  <ContextMenuRadioItem
                    value="never"
                    onClick={() => onSetFolderVisibility(firstFolder.accountId, firstFolder.folderId, 'never')}
                  >
                    Never show
                  </ContextMenuRadioItem>
                </ContextMenuRadioGroup>
              </ContextMenuSubContent>
            </ContextMenuSub>
          )}
          {canRename && (
            <ContextMenuItem
              onClick={() => onRenameFolder(firstFolder.accountId, firstFolder.folderId, folderName ?? '')}
            >
              <Pencil className="h-4 w-4" />
              Rename folder
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  const renderCustomFolderItem = (folder: Folder, options?: { muted?: boolean }) => {
    const { muted = false } = options ?? {};
    const sources = getFolderSources(folder);
    const isAccountSpecific = sources.length === 1 && sources[0].accountId !== UNIFIED_ACCOUNT_ID;
    const source = isAccountSpecific ? sources[0] : null;
    const key = getCompositeKeyForCustomFolder(folder);
    const isActive = selectedCompositeKey === key;
    const Icon = (folder.icon && (fallbackIconMap[folder.icon] ?? Inbox)) || Inbox;
    const badge =
      (folder.count ?? 0) > 0 ? <Badge variant={isActive ? 'default' : 'secondary'}>{folder.count}</Badge> : null;
    const label = folder.name;
    const targetAccountId = source ? source.accountId : UNIFIED_ACCOUNT_ID;
    const targetFolderId = source ? source.folderId : folder.id;
    const isGroup = !isAccountSpecific && sources.length > 1;
    const accountEntries = isGroup
      ? sources
          .map((entry) => {
            if (entry.accountId === UNIFIED_ACCOUNT_ID) {
              return null;
            }
            const account = accountsById[entry.accountId];
            if (!account) {
              return null;
            }
            const accountFolder = (accountFoldersById[entry.accountId] ?? []).find(
              (candidate) => candidate.id === entry.folderId,
            );
            if (!accountFolder) {
              return null;
            }
            return { account, folder: accountFolder };
          })
          .filter((value): value is { account: Account; folder: Folder } => Boolean(value))
          .sort((a, b) => {
            const aName = a.account.displayName || a.account.emailAddress || '';
            const bName = b.account.displayName || b.account.emailAddress || '';
            return aName.localeCompare(bName);
          })
      : [];
    const hasAccountEntries = accountEntries.length > 0;
    const autoExpanded =
      isGroup && hasAccountEntries
        ? accountEntries.some(
            ({ account, folder: accountFolder }) => `${account.id}:${accountFolder.id}` === selectedCompositeKey,
          )
        : false;
    const manualState = expandedCustomGroups[folder.id];
    const isExpanded = isGroup && hasAccountEntries ? manualState ?? autoExpanded : false;

    const button = renderFolderButton(folder, {
      key: isGroup ? undefined : key,
      label: (
        <span className="flex items-center gap-2">
          {isGroup && hasAccountEntries ? (
            <div
              role="button"
              tabIndex={0}
              className="relative h-4 w-4 rounded-full hover:bg-foreground/10 inline-flex items-center justify-center cursor-pointer transition-colors pointer-coarse:before:absolute pointer-coarse:before:-inset-3 pointer-coarse:before:content-[''] shrink-0"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleCustomGroupExpansion(folder.id, autoExpanded);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  toggleCustomGroupExpansion(folder.id, autoExpanded);
                }
              }}
            >
              <ChevronRight
                className={cn('h-3.5 w-3.5 transition-transform text-muted-foreground', isExpanded ? 'rotate-90' : 'rotate-0')}
              />
            </div>
          ) : (
            <span className="w-4 shrink-0" />
          )}
          <Icon className="h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
        </span>
      ),
      badge,
      isActive,
      className: cn(muted && 'opacity-70'),
      onClick: () => handleAccountFolderClick(targetAccountId, targetFolderId),
      onHover: () => {
        if (targetAccountId !== UNIFIED_ACCOUNT_ID) {
          onFolderHover?.(targetAccountId, targetFolderId);
        }
      },
      // Add context menu - for unified groups, include all source folders for mark-all-as-read
      contextMenu: isAccountSpecific
        ? {
            folders: [{ accountId: targetAccountId, folderId: targetFolderId }],
            visibility: getFolderVisibility(folder),
            canHide: true,
            role: folder.role ?? null,
            unifiedRole: folder.unifiedRole ?? null,
            folderName: folder.name,
            isSystemFolder: folder.systemFolder ?? false,
          }
        : isGroup && hasAccountEntries
          ? {
              folders: accountEntries.map(({ account, folder: f }) => ({
                accountId: account.id,
                folderId: f.id,
              })),
              canHide: false,
              isSystemFolder: true, // Unified groups can't be renamed individually
            }
          : undefined,
    });

    if (!isGroup || !hasAccountEntries) {
      return button;
    }

    return (
      <div key={key} className="space-y-1">
        {button}
        {isExpanded && (
          <div className="ml-8 mt-1 space-y-1">
            {accountEntries.map(({ account, folder: accountFolder }) => {
              const accountKey = `${account.id}:${accountFolder.id}`;
              const accountBadge =
                (accountFolder.count ?? 0) > 0 ? (
                  <Badge variant={selectedCompositeKey === accountKey ? 'default' : 'secondary'}>
                    {accountFolder.count}
                  </Badge>
                ) : null;
              return renderFolderButton(accountFolder, {
                key: accountKey,
                label: (
                  <span className="flex items-center gap-2">
                    <Avatar className="h-6 w-6 border border-border/50">
                      <AvatarFallback
                        style={account.color ? { backgroundColor: account.color } : undefined}
                        className={cn(
                          'text-[10px] font-semibold uppercase',
                          account.color ? 'text-white' : 'bg-muted text-foreground',
                        )}
                      >
                        {getInitials(account)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate text-sm">{account.displayName || account.emailAddress}</span>
                  </span>
                ),
                badge: accountBadge,
                isActive: selectedCompositeKey === accountKey,
                className: cn('pl-1', muted && 'opacity-70'),
                onClick: () => handleAccountFolderClick(account.id, accountFolder.id),
                onHover: () => onFolderHover?.(account.id, accountFolder.id),
                contextMenu: {
                  folders: [{ accountId: account.id, folderId: accountFolder.id }],
                  visibility: getFolderVisibility(accountFolder),
                  canHide: true,
                  role: accountFolder.role ?? null,
                  unifiedRole: accountFolder.unifiedRole ?? null,
                  folderName: accountFolder.name,
                  isSystemFolder: accountFolder.systemFolder ?? false,
                },
              });
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 backdrop-blur-xs animate-fade-in-up md:hidden"
          onClick={onMobileClose}
        />
      )}

      <div
        className={cn(
          'flex h-full flex-col w-64 md:w-55 shrink-0 surface-dark',
          // The mobile drawer is fixed to the viewport, outside MailLayout's
          // pwa-safe-area padding — pad the notch inset itself.
          'fixed inset-y-0 -left-4 md:left-0 z-50 transform transition-transform duration-200 ease-in-out md:relative md:translate-x-0 max-md:pt-[env(safe-area-inset-top,0px)]',
          isMobileOpen
            ? 'translate-x-4 rounded-r-2xl shadow-2xl'
            : '-translate-x-full md:border-0',
        )}
      >
        <div className="flex items-center justify-between p-3 md:hidden pb-0 md:pb-3">
          <Button variant="ghost" size="icon" onClick={onMobileClose} className="pl-2">
            <X className="size-6" />
          </Button>
        </div>

        <div className="hidden md:block p-4 pt-2 md:pt-4 space-y-3">
          {/* App switcher moved to the combined top row (MailViewTabs / page
              headers); the rail is pure workspace navigation now. */}
          <Button
            onClick={handlePrimaryActionClick}
            className="w-full gap-2 font-semibold hidden md:flex"
            size="lg"
            variant="compose"
          >
            {primaryActionContext === 'task' ? (
              <PlusCircle className="h-5 w-5" />
            ) : primaryActionContext === 'event' ? (
              <CalendarPlus className="h-5 w-5" />
            ) : primaryActionContext === 'note' ? (
              <FilePlus className="h-5 w-5" />
            ) : (
              <PenSquare className="h-5 w-5" />
            )}
            {primaryActionLabel}
          </Button>
        </div>

        <Separator className="hidden md:block"/>

        <ScrollArea className="flex-1" viewportRef={railViewportRef}>
          {contentOverride !== undefined ? (
            contentOverride
          ) : isTodoWorkspace ? (
            <TodoSidebarContent />
          ) : isCalendarWorkspace ? (
            <CalendarSidebarContent />
          ) : isNotesWorkspace ? (
            <NotesSidebarContent />
          ) : (
            <div className="p-2 pt-2 md:pt-4">
              <div className="space-y-1">
                {systemGroups.map((group) => {
                const Icon = standardRoleIconMap[group.role] ?? Inbox;
                const isExpanded = expandedGroups[group.role] ?? autoExpandedByRole[group.role] ?? false;
                const isActive =
                  (selectedAccountId === UNIFIED_ACCOUNT_ID && selectedFolderId === group.unifiedFolder.id)
                  || group.accountFolders.some(
                    ({ account, folder }) =>
                      account.id === selectedAccountId && folder.id === selectedFolderId,
                  );

                const badge =
                  (group.unifiedFolder.count ?? 0) > 0 ? (
                    <Badge variant={isActive ? 'default' : 'secondary'}>
                      {group.unifiedFolder.count}
                    </Badge>
                  ) : null;

                return (
                  <div key={group.role} className="space-y-1">
                    {renderFolderButton(group.unifiedFolder, {
                      label: (
                        <span className="flex items-center gap-2">
                          {group.accountFolders.length > 0 ? (
                            <div
                              role="button"
                              tabIndex={0}
                              aria-label={isExpanded ? 'Collapse account folders' : 'Expand account folders'}
                              aria-expanded={isExpanded}
                              className="relative h-4 w-4 rounded-full hover:bg-foreground/10 inline-flex items-center justify-center cursor-pointer transition-colors pointer-coarse:before:absolute pointer-coarse:before:-inset-3 pointer-coarse:before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 shrink-0"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleGroup(group.role);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  toggleGroup(group.role);
                                }
                              }}
                            >
                              <ChevronRight
                                className={cn(
                                  'h-3.5 w-3.5 transition-transform text-muted-foreground',
                                  isExpanded ? 'rotate-90' : 'rotate-0',
                                )}
                              />
                            </div>
                          ) : (
                            <span className="w-4 shrink-0" />
                          )}
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{getStandardRoleDisplayName(group.role)}</span>
                        </span>
                      ),
                      badge,
                      isActive,
                      onClick: () => handleUnifiedClick(group.unifiedFolder),
                      // Add context menu for unified folders - include all account folders for mark-all-as-read
                      contextMenu: group.accountFolders.length > 0
                        ? {
                            folders: group.accountFolders.map(({ account, folder }) => ({
                              accountId: account.id,
                              folderId: folder.id,
                            })),
                            canHide: false, // System folders can't be hidden
                            role: group.role,
                            unifiedRole: group.role,
                            folderName: group.unifiedFolder.name,
                            isSystemFolder: true, // Unified system folders
                          }
                        : undefined,
                    })}

                    {isExpanded && group.accountFolders.length > 0 && (
                      <div className="ml-8 mt-1 space-y-1">
                        {group.accountFolders.map(({ account, folder }) => {
                          const accountKey = `${account.id}:${folder.id}`;
                          const accountBadge =
                            (folder.count ?? 0) > 0 ? (
                              <Badge variant={selectedCompositeKey === accountKey ? 'default' : 'secondary'}>
                                {folder.count}
                              </Badge>
                            ) : null;

                          return renderFolderButton(folder, {
                            key: accountKey,
                            label: (
                              <span className="flex items-center gap-2">
                                <Avatar className="h-6 w-6 border border-border/50">
                                  <AvatarFallback
                                    style={account.color ? { backgroundColor: account.color } : undefined}
                                    className={cn(
                                      'text-[10px] font-semibold uppercase',
                                      account.color ? 'text-white' : 'bg-muted text-foreground',
                                    )}
                                  >
                                    {getInitials(account)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="truncate text-sm">
                                  {account.displayName || account.emailAddress}
                                </span>
                              </span>
                            ),
                            badge: accountBadge,
                            isActive: selectedCompositeKey === accountKey,
                            className: 'pl-1',
                            onClick: () => handleAccountFolderClick(account.id, folder.id),
                            onHover: () => onFolderHover?.(account.id, folder.id),
                            contextMenu: {
                              folders: [{ accountId: account.id, folderId: folder.id }],
                              visibility: getFolderVisibility(folder),
                              canHide: false, // System folders shouldn't be hidden
                              role: folder.role ?? null,
                              unifiedRole: folder.unifiedRole ?? null,
                              folderName: folder.name,
                              isSystemFolder: true, // These are expanded system folder items
                            },
                          });
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {(visibleCustomFolders.length > 0 || moreCustomFolders.length > 0) && (
              <>
                <Separator className="my-4" />
                <div className="space-y-1">
                  <div className="px-3 py-2 text-xs font-semibold text-sidebar-foreground/70">
                    {selectedAccountId === UNIFIED_ACCOUNT_ID ? 'FOLDERS' : 'FOLDERS'}
                  </div>
                  {visibleCustomFolders.map((folder) => renderCustomFolderItem(folder))}

                  {moreCustomFolders.length > 0 && (
                    <div className="mt-1 space-y-1">
                      <Button
                        variant="ghost"
                        onClick={() => setMoreExpanded((prev) => !prev)}
                        className="w-full justify-start gap-2 font-normal"
                      >
                        <span className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full hover:bg-foreground/10 inline-flex items-center justify-center cursor-pointer transition-colors shrink-0">

                            </div>
                            <MoreHorizontal className="h-4 w-4 shrink-0" />
                          <span className="truncate">More</span>
                        </span>
                      </Button>
                      {isMoreSectionExpanded && (
                        <div className="space-y-1">
                          {moreCustomFolders.map((folder) => renderCustomFolderItem(folder, { muted: true }))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          )}
        </ScrollArea>

        <div className="p-4 space-y-3">
          {!isTodoWorkspace && !isNotesWorkspace && syncIndicators && syncIndicators.length > 0 && (
            <div className="space-y-2 max-h-90 overflow-y-auto pr-1">
              <SyncProgressBar key={`${syncIndicators[0].label}-0`} {...syncIndicators[0]} />
            </div>
          )}

          {/* Recent opens — mailbox and briefing tabs only */}
          {activeWorkspace === 'mail' &&
            (mailView === 'mailbox' || mailView === 'briefing') &&
            selectedAccountId && (
              <RecentOpensPopover
                accountId={selectedAccountId}
                limit={20}
                side="right"
                align="end"
                sideOffset={12}
                onMobileOpen={onMobileClose}
                trigger={
                  <Button variant="ghost" className="w-full justify-start gap-3">
                    <FolderClock className="h-4 w-4 shrink-0" />
                    Recent Opens
                  </Button>
                }
              />
            )}

          <Separator />

          <Button
            variant="ghost"
            className="w-full justify-start gap-3"
            onClick={() => {
              onOpenSettings('general');
              onMobileClose?.();
            }}
          >
            <Settings className="h-4 w-4" />
            Settings
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={logout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>
    </>
  );
}
